import { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SPONSORSHIP_QUERY_KEYS } from "@/features/sponsorship/constants/queryKeys";
import type { CreateRecommendationFormData } from "@/features/sponsorship/schemas/createRecommendation.schema";
import { sponsorshipService } from "@/features/sponsorship/services/sponsorship.service";
import type {
  CreateSponsorshipPayload,
  SponsorshipValidationError,
} from "@/features/sponsorship/types/sponsorship.type";

export const useCreateRecommendation = () => {
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<SponsorshipValidationError | null>(null);

  const mutation = useMutation({
    mutationFn: async (formData: CreateRecommendationFormData) => {
      // L'API refuse la création tant qu'aucune acceptation du règlement en
      // vigueur n'existe en base : la case cochée dans le formulaire doit
      // être matérialisée via l'endpoint dédié (idempotent) avant le create.
      const { sponsorship_term } = await sponsorshipService
        .getCurrentTerms()
        .catch(() => ({ sponsorship_term: null }));

      if (sponsorship_term && !sponsorship_term.accepted) {
        await sponsorshipService.acceptTerms(sponsorship_term.id);
      }

      const payload: CreateSponsorshipPayload = { sponsorship_request: formData };
      return sponsorshipService.createSponsorshipRequest(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPONSORSHIP_QUERY_KEYS.recommendations() });
      queryClient.invalidateQueries({ queryKey: SPONSORSHIP_QUERY_KEYS.campaign() });
      queryClient.invalidateQueries({ queryKey: SPONSORSHIP_QUERY_KEYS.balance() });
      queryClient.invalidateQueries({ queryKey: SPONSORSHIP_QUERY_KEYS.terms() });
      toast.success("Parrainage créé avec succès");
      setServerErrors(null);
    },
    onError: (error: Error) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        const validationError = error.response.data as SponsorshipValidationError;
        setServerErrors(validationError);

        const firstError = validationError.errors?.[0];
        toast.error(firstError ?? "Certains champs contiennent des erreurs");
        return;
      }

      toast.error("Une erreur est survenue lors de la création du parrainage");
    },
  });

  return {
    createRecommendation: mutation.mutateAsync,
    isPending: mutation.isPending,
    serverErrors,
    clearServerErrors: () => setServerErrors(null),
  };
};
